import React from 'react';
import { Icon } from './AdminShell';

/*
 * Overlay bersama: modal konfirmasi, drawer detail, checklist, ringkasan,
 * lini masa, tab.
 *
 * Diport dari `prototype/js/overlay.js` + `ui.js` paket client, memakai nama
 * kelas dari `overlays.css` supaya markupnya bisa dipindah nyaris apa adanya.
 *
 * Dibuat sekali karena dipakai jauh melampaui satu layar: konfirmasi penugasan
 * massal, drawer detail booking, persetujuan provider, pembatalan berdasar
 * alasan, dan hampir setiap aksi yang tidak bisa dibatalkan.
 *
 * Esc menutup, dan fokus dikembalikan ke elemen yang membukanya — keduanya
 * gratis di sini dan mahal kalau diulang di tiap pemanggil.
 */

class Layer extends React.Component {
  componentDidMount() {
    this.opener = document.activeElement;
    this.onKey = e => { if (e.key === 'Escape') this.props.onClose(); };
    document.addEventListener('keydown', this.onKey);
    // Halaman di belakang tidak boleh ikut menggulir saat overlay terbuka.
    this.prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKey);
    document.body.style.overflow = this.prevOverflow || '';
    if (this.opener && this.opener.focus) this.opener.focus();
  }
  render() { return this.props.children; }
}

export const Checklist = ({ items }) => (
  <div className="chk">
    {items.map((it, i) => (
      <div className={`chk-row ${it.state || 'pass'}`} key={i}>
        <div className="ci" aria-hidden="true">
          <Icon name={it.state === 'fail' ? 'x' : it.state === 'warn' ? 'clock' : 'check'} />
        </div>
        <div className="cl">
          {it.label}
          {it.detail && <span dir="auto">{it.detail}</span>}
        </div>
      </div>
    ))}
  </div>
);

export const Summary = ({ pairs }) => (
  <div className="sumbox">
    <dl className="def">
      {pairs.map(([k, v], i) => [
        <dt key={`t${i}`}>{k}</dt>,
        <dd key={`d${i}`} dir="auto">{v}</dd>
      ])}
    </dl>
  </div>
);

export const Timeline = ({ events }) => (
  <div className="timeline">
    {events.map((e, i) => (
      <div className="tl-item" key={i}>
        <span className="tl-dot" style={{ background: 'var(--surface-3)' }}>
          <i style={{ background: e.color || 'var(--brand-amber)' }} />
        </span>
        <div className="tl-body">
          <b>{e.title}</b>
          {e.detail && <span>{e.detail}</span>}
          {e.at && <time>{e.at}</time>}
        </div>
      </div>
    ))}
  </div>
);

export const Tabs = ({ labels, active, onChange }) => (
  <div className="tabs" role="tablist">
    {labels.map((l, i) => (
      <button key={l} type="button" role="tab"
        aria-selected={String(i === active)}
        onClick={() => onChange(i)}>{l}</button>
    ))}
  </div>
);

/*
 * Modal konfirmasi. `tone` mewarnai ikon kepalanya: kosong, 'danger', 'ok'.
 * Tombol konfirmasi TIDAK diberi fokus otomatis — aksi yang tidak bisa
 * dibatalkan tidak boleh bisa dijalankan dengan satu tekan Enter yang refleks.
 */
export const Modal = ({
  title, sub, icon = 'check', tone = '', wide, narrow,
  children, confirmLabel = 'Confirm', onConfirm, onClose, busy, disabled, hideConfirm,
  cancelLabel = 'Cancel', workingLabel = 'Working…', showClose
}) => (
  <Layer onClose={onClose}>
    <div className="scrim" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal${wide ? ' wide' : ''}${narrow ? ' narrow' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className={`modal-h ${tone}`}>
          <span className="mi" aria-hidden="true"><Icon name={icon} /></span>
          <div className="mt">
            <h2>{title}</h2>
            {sub && <p>{sub}</p>}
          </div>
          {showClose && (
            <button className="btn icon" type="button" onClick={onClose} aria-label={cancelLabel}>
              <Icon name="x" />
            </button>
          )}
        </div>
        <div className="modal-b">{children}</div>
        <div className="modal-f">
          <div className="modal-actions">
            <button className="btn ghost" type="button" onClick={onClose}>{cancelLabel}</button>
            {/* `busy` dan `disabled` dipisah dengan sengaja: tombol yang belum
                boleh ditekan karena formulirnya kosong TIDAK sedang bekerja, dan
                menulis "Working…" di situ membuat orang mengira perintahnya
                sudah berjalan. */}
            {!hideConfirm && (
              <button className={`btn${tone === 'danger' ? ' danger' : ''}`} type="button"
                onClick={onConfirm} disabled={busy || disabled}>
                {busy ? workingLabel : confirmLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  </Layer>
);

/*
 * Drawer kanan untuk detail. Dipakai untuk apa pun yang menjawab "ceritakan
 * lebih lanjut tentang baris ini" tanpa meninggalkan daftarnya — kehilangan
 * posisi gulir dan filter demi melihat satu baris itu ongkos yang tidak perlu.
 */
export const Drawer = ({ title, sub, headExtra, tabs, children, actions, onClose, closeLabel = 'Close' }) => (
  <Layer onClose={onClose}>
    <div className="drawer-scrim" onClick={e => { if (e.target === e.currentTarget) onClose(); }} />
    <aside className="drawer" role="dialog" aria-modal="true" aria-label={title}>
      <div className="drawer-h">
        <div className="dt">
          <h2>{title}</h2>
          {sub && <p>{sub}</p>}
        </div>
        {headExtra}
        <button className="btn icon" type="button" onClick={onClose} aria-label={closeLabel}>
          <Icon name="x" />
        </button>
      </div>
      {tabs}
      <div className="drawer-b">{children}</div>
      <div className="drawer-f">{actions}</div>
    </aside>
  </Layer>
);

export const EmptyState = ({ icon = 'inbox', title, msg }) => (
  <div className="empty">
    <span className="ei"><Icon name={icon} /></span>
    <b>{title}</b>
    {msg && <p style={{ marginTop: 6, fontSize: 'var(--fs-sm)' }}>{msg}</p>}
  </div>
);
